// Stat Categories
export const STAT_CATEGORIES = {
  HITTING: "hitting",
  PITCHING: "pitching",
  FIELDING: "fielding",
};

// Hitting Stat Fields (for leaderboard mapping)
export const HITTING_STATS = {
  BATTING_AVERAGE: "battingAverage",
  HOME_RUNS: "homeruns",
  HITS: "hits",
  RBI: "rbis", // Leaderboard endpoint uses 'rbis'
  ON_BASE_PERCENTAGE: "onBasePercentage",
  SLUGGING_AVERAGE: "sluggingAverage",
  RUNS: "runs",
  DOUBLES: "doubles",
  TRIPLES: "triples",
  WALKS: "walks",
  STRIKEOUTS: "strikeouts",
  STOLEN_BASES: "stolenBases",
};

// Player Data Stat Fields (for player data mapping)
export const PLAYER_HITTING_STATS = {
  BATTING_AVERAGE: "battingAverage",
  HOME_RUNS: "homeruns",
  HITS: "hits",
  RBI: "runBattedIn", // Player data uses 'runBattedIn'
  ON_BASE_PERCENTAGE: "onBasePercentage",
  SLUGGING_AVERAGE: "sluggingAverage",
  RUNS: "runs",
  DOUBLES: "doubles",
  TRIPLES: "triples",
  WALKS: "baseOnBalls", // Player data uses 'baseOnBalls'
  STRIKEOUTS: "strikeOut", // Player data uses 'strikeOut'
  STOLEN_BASES: "stolenBases",
};

// Helper function to get player data field name from leaderboard stat name
export const getPlayerDataFieldName = (leaderboardStatName) => {
  return (
    LEADERBOARD_TO_PLAYER_FIELD_MAP[leaderboardStatName] || leaderboardStatName
  );
};

// Pitching Stat Fields (for leaderboard mapping)
export const PITCHING_STATS = {
  WINS: "wins",
  LOSSES: "losses",
  ERA: "era",
  STRIKEOUTS: "strikeOuts",
  WALKS: "baseOnBalls",
  HITS_ALLOWED: "hitsAllowed",
  EARNED_RUNS: "earnedRuns",
  COMPLETE_GAMES: "completeGames",
  SHUTOUTS: "shutouts",
  SAVES: "saves",
  INNINGS_PITCHED: "inningsPitched",
  HOME_RUNS_ALLOWED: "homeRunsAllowed",
};

// Player Data Pitching Stat Fields (for player data mapping)
export const PLAYER_PITCHING_STATS = {
  WINS: "wins",
  LOSSES: "losses",
  ERA: "era",
  STRIKEOUTS: "strikeOuts",
  WALKS: "baseOnBalls",
  HITS_ALLOWED: "hits", // Player data uses 'hits' for hits allowed
  EARNED_RUNS: "earnedRuns",
  COMPLETE_GAMES: "completeGames",
  SHUTOUTS: "shutouts",
  SAVES: "saves",
  INNINGS_PITCHED: "inningsPitched",
  HOME_RUNS_ALLOWED: "homeRuns", // Player data uses 'homeRuns' for home runs allowed
};

// Fielding Stat Fields
export const FIELDING_STATS = {
  GAMES: "g",
  CHANCES: "ch",
  PUTOUTS: "po",
  ASSISTS: "a",
  ERRORS: "e",
  FIELDING_PERCENTAGE: "FLD%",
  RANGE_FACTOR: "rf",
};

// Leaderboard Stat Categories (for useLeaderboardData)
export const LEADERBOARD_CATEGORIES = {
  hitting: [
    { name: HITTING_STATS.HOME_RUNS, displayName: "Home Runs" },
    { name: HITTING_STATS.BATTING_AVERAGE, displayName: "Batting Average" },
    { name: HITTING_STATS.RBI, displayName: "RBIs" },
    { name: HITTING_STATS.HITS, displayName: "Hits" },
    { name: HITTING_STATS.ON_BASE_PERCENTAGE, displayName: "On-Base %" },
    { name: HITTING_STATS.SLUGGING_AVERAGE, displayName: "Slugging %" },
    { name: HITTING_STATS.RUNS, displayName: "Runs" },
    { name: HITTING_STATS.DOUBLES, displayName: "Doubles" },
    { name: HITTING_STATS.TRIPLES, displayName: "Triples" },
    { name: HITTING_STATS.WALKS, displayName: "Walks" },
    { name: HITTING_STATS.STRIKEOUTS, displayName: "Strikeouts" },
  ],
  pitching: [
    { name: PITCHING_STATS.WINS, displayName: "Wins" },
    { name: PITCHING_STATS.ERA, displayName: "ERA" },
    { name: PITCHING_STATS.STRIKEOUTS, displayName: "Strikeouts" },
    { name: PITCHING_STATS.WALKS, displayName: "Walks" },
    { name: PITCHING_STATS.HITS_ALLOWED, displayName: "Hits Allowed" },
    { name: PITCHING_STATS.EARNED_RUNS, displayName: "Earned Runs" },
    { name: PITCHING_STATS.COMPLETE_GAMES, displayName: "Complete Games" },
    { name: PITCHING_STATS.SHUTOUTS, displayName: "Shutouts" },
    { name: PITCHING_STATS.SAVES, displayName: "Saves" },
    { name: PITCHING_STATS.INNINGS_PITCHED, displayName: "Innings Pitched" },
    {
      name: PITCHING_STATS.HOME_RUNS_ALLOWED,
      displayName: "Home Runs Allowed",
    },
  ],
  fielding: [
    { name: FIELDING_STATS.GAMES, displayName: "Games" },
    { name: FIELDING_STATS.CHANCES, displayName: "Chances" },
    { name: FIELDING_STATS.PUTOUTS, displayName: "Putouts" },
    { name: FIELDING_STATS.ASSISTS, displayName: "Assists" },
    { name: FIELDING_STATS.ERRORS, displayName: "Errors" },
    { name: FIELDING_STATS.FIELDING_PERCENTAGE, displayName: "Fielding %" },
    { name: FIELDING_STATS.RANGE_FACTOR, displayName: "Range Factor" },
  ],
};

// Stat Display Labels
export const STAT_LABELS = {
  // Hitting
  [HITTING_STATS.BATTING_AVERAGE]: "Batting Average",
  [HITTING_STATS.HOME_RUNS]: "Home Runs",
  [HITTING_STATS.HITS]: "Hits",
  [HITTING_STATS.RBI]: "RBI",
  [HITTING_STATS.ON_BASE_PERCENTAGE]: "On-Base %",
  [HITTING_STATS.SLUGGING_AVERAGE]: "Slugging %",
  [HITTING_STATS.RUNS]: "Runs",
  [HITTING_STATS.DOUBLES]: "Doubles",
  [HITTING_STATS.TRIPLES]: "Triples",
  [HITTING_STATS.WALKS]: "Walks",
  [HITTING_STATS.STRIKEOUTS]: "Strikeouts",
  [HITTING_STATS.STOLEN_BASES]: "Stolen Bases",

  // Pitching
  [PITCHING_STATS.WINS]: "Wins",
  [PITCHING_STATS.LOSSES]: "Losses",
  [PITCHING_STATS.ERA]: "ERA",
  [PITCHING_STATS.STRIKEOUTS]: "Strikeouts",
  [PITCHING_STATS.WALKS]: "Walks",
  [PITCHING_STATS.HITS_ALLOWED]: "Hits Allowed",
  [PITCHING_STATS.EARNED_RUNS]: "Earned Runs",
  [PITCHING_STATS.COMPLETE_GAMES]: "Complete Games",
  [PITCHING_STATS.SHUTOUTS]: "Shutouts",
  [PITCHING_STATS.SAVES]: "Saves",
  [PITCHING_STATS.INNINGS_PITCHED]: "Innings Pitched",
  [PITCHING_STATS.HOME_RUNS_ALLOWED]: "Home Runs Allowed",

  // Fielding
  [FIELDING_STATS.GAMES]: "Games",
  [FIELDING_STATS.CHANCES]: "Chances",
  [FIELDING_STATS.PUTOUTS]: "Putouts",
  [FIELDING_STATS.ASSISTS]: "Assists",
  [FIELDING_STATS.ERRORS]: "Errors",
  [FIELDING_STATS.FIELDING_PERCENTAGE]: "Fielding %",
  [FIELDING_STATS.RANGE_FACTOR]: "Range Factor",
};

// Stat Options Configuration
export const STAT_OPTIONS = [
  // Hitting stats
  {
    value: HITTING_STATS.BATTING_AVERAGE,
    label: STAT_LABELS[HITTING_STATS.BATTING_AVERAGE],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.HOME_RUNS,
    label: STAT_LABELS[HITTING_STATS.HOME_RUNS],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.HITS,
    label: STAT_LABELS[HITTING_STATS.HITS],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.RBI,
    label: STAT_LABELS[HITTING_STATS.RBI],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.ON_BASE_PERCENTAGE,
    label: STAT_LABELS[HITTING_STATS.ON_BASE_PERCENTAGE],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.SLUGGING_AVERAGE,
    label: STAT_LABELS[HITTING_STATS.SLUGGING_AVERAGE],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.RUNS,
    label: STAT_LABELS[HITTING_STATS.RUNS],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.DOUBLES,
    label: STAT_LABELS[HITTING_STATS.DOUBLES],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.TRIPLES,
    label: STAT_LABELS[HITTING_STATS.TRIPLES],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.WALKS,
    label: STAT_LABELS[HITTING_STATS.WALKS],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.STRIKEOUTS,
    label: STAT_LABELS[HITTING_STATS.STRIKEOUTS],
    category: STAT_CATEGORIES.HITTING,
  },
  {
    value: HITTING_STATS.STOLEN_BASES,
    label: STAT_LABELS[HITTING_STATS.STOLEN_BASES],
    category: STAT_CATEGORIES.HITTING,
  },

  // Pitching stats
  {
    value: PITCHING_STATS.WINS,
    label: STAT_LABELS[PITCHING_STATS.WINS],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.LOSSES,
    label: STAT_LABELS[PITCHING_STATS.LOSSES],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.ERA,
    label: STAT_LABELS[PITCHING_STATS.ERA],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.STRIKEOUTS,
    label: STAT_LABELS[PITCHING_STATS.STRIKEOUTS],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.WALKS,
    label: STAT_LABELS[PITCHING_STATS.WALKS],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.HITS_ALLOWED,
    label: STAT_LABELS[PITCHING_STATS.HITS_ALLOWED],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.EARNED_RUNS,
    label: STAT_LABELS[PITCHING_STATS.EARNED_RUNS],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.COMPLETE_GAMES,
    label: STAT_LABELS[PITCHING_STATS.COMPLETE_GAMES],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.SHUTOUTS,
    label: STAT_LABELS[PITCHING_STATS.SHUTOUTS],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.SAVES,
    label: STAT_LABELS[PITCHING_STATS.SAVES],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.INNINGS_PITCHED,
    label: STAT_LABELS[PITCHING_STATS.INNINGS_PITCHED],
    category: STAT_CATEGORIES.PITCHING,
  },
  {
    value: PITCHING_STATS.HOME_RUNS_ALLOWED,
    label: STAT_LABELS[PITCHING_STATS.HOME_RUNS_ALLOWED],
    category: STAT_CATEGORIES.PITCHING,
  },

  // Fielding stats
  {
    value: FIELDING_STATS.GAMES,
    label: STAT_LABELS[FIELDING_STATS.GAMES],
    category: STAT_CATEGORIES.FIELDING,
  },
  {
    value: FIELDING_STATS.CHANCES,
    label: STAT_LABELS[FIELDING_STATS.CHANCES],
    category: STAT_CATEGORIES.FIELDING,
  },
  {
    value: FIELDING_STATS.PUTOUTS,
    label: STAT_LABELS[FIELDING_STATS.PUTOUTS],
    category: STAT_CATEGORIES.FIELDING,
  },
  {
    value: FIELDING_STATS.ASSISTS,
    label: STAT_LABELS[FIELDING_STATS.ASSISTS],
    category: STAT_CATEGORIES.FIELDING,
  },
  {
    value: FIELDING_STATS.ERRORS,
    label: STAT_LABELS[FIELDING_STATS.ERRORS],
    category: STAT_CATEGORIES.FIELDING,
  },
  {
    value: FIELDING_STATS.FIELDING_PERCENTAGE,
    label: STAT_LABELS[FIELDING_STATS.FIELDING_PERCENTAGE],
    category: STAT_CATEGORIES.FIELDING,
  },
  {
    value: FIELDING_STATS.RANGE_FACTOR,
    label: STAT_LABELS[FIELDING_STATS.RANGE_FACTOR],
    category: STAT_CATEGORIES.FIELDING,
  },
];

// Season DataGrid Column Definitions
export const SEASON_COLUMNS = {
  [STAT_CATEGORIES.HITTING]: [
    { field: "year", headerName: "Year", width: 100 },
    { field: "team", headerName: "Team", width: 120 },
    {
      field: "games",
      headerName: "Games",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "atBat",
      headerName: "AB",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "hits",
      headerName: "Hits",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "homeruns",
      headerName: "HR",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "runBattedIn",
      headerName: "RBI",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "battingAverage",
      headerName: "Avg",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "onBasePercentage",
      headerName: "OBP",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "sluggingAverage",
      headerName: "SLG",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "stolenBases",
      headerName: "SB",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
  ],
  [STAT_CATEGORIES.PITCHING]: [
    { field: "year", headerName: "Year", width: 100 },
    { field: "team", headerName: "Team", width: 120 },
    {
      field: "games",
      headerName: "Games",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "gamesStarted",
      headerName: "GS",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "wins",
      headerName: "W",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "losses",
      headerName: "L",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "era",
      headerName: "ERA",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "strikeOuts",
      headerName: "SO",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "baseOnBalls",
      headerName: "BB",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "hits",
      headerName: "H",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "earnedRuns",
      headerName: "ER",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "inningsPitched",
      headerName: "IP",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
  ],
  [STAT_CATEGORIES.FIELDING]: [
    { field: "year", headerName: "Year", width: 100 },
    { field: "team", headerName: "Team", width: 120 },
    { field: "pos", headerName: "Pos", width: 80 },
    {
      field: "g",
      headerName: "Games",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "gs",
      headerName: "GS",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "ch",
      headerName: "Ch",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "po",
      headerName: "PO",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "a",
      headerName: "A",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "e",
      headerName: "E",
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "FLD%",
      headerName: "FLD%",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "rf",
      headerName: "RF",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
  ],
};

// Helper function to get stat label
export const getStatLabel = (statValue) => {
  return STAT_LABELS[statValue] || statValue;
};

// Helper function to get stat options by category
export const getStatOptionsByCategory = (category) => {
  return STAT_OPTIONS.filter((option) => option.category === category);
};

// Helper function to get season columns by category
export const getSeasonColumnsByCategory = (category) => {
  return SEASON_COLUMNS[category] || [];
};

// Mapping from leaderboard stat names to player data field names
export const LEADERBOARD_TO_PLAYER_FIELD_MAP = {
  // Hitting stats
  [HITTING_STATS.BATTING_AVERAGE]: PLAYER_HITTING_STATS.BATTING_AVERAGE,
  [HITTING_STATS.HOME_RUNS]: PLAYER_HITTING_STATS.HOME_RUNS,
  [HITTING_STATS.HITS]: PLAYER_HITTING_STATS.HITS,
  [HITTING_STATS.RBI]: PLAYER_HITTING_STATS.RBI, // "rbis" -> "runBattedIn"
  [HITTING_STATS.ON_BASE_PERCENTAGE]: PLAYER_HITTING_STATS.ON_BASE_PERCENTAGE,
  [HITTING_STATS.SLUGGING_AVERAGE]: PLAYER_HITTING_STATS.SLUGGING_AVERAGE,
  [HITTING_STATS.RUNS]: PLAYER_HITTING_STATS.RUNS,
  [HITTING_STATS.DOUBLES]: PLAYER_HITTING_STATS.DOUBLES,
  [HITTING_STATS.TRIPLES]: PLAYER_HITTING_STATS.TRIPLES,
  [HITTING_STATS.WALKS]: PLAYER_HITTING_STATS.WALKS, // "walks" -> "baseOnBalls"
  [HITTING_STATS.STRIKEOUTS]: PLAYER_HITTING_STATS.STRIKEOUTS, // "strikeouts" -> "strikeOut"
  [HITTING_STATS.STOLEN_BASES]: PLAYER_HITTING_STATS.STOLEN_BASES,

  // Pitching stats
  [PITCHING_STATS.WINS]: PLAYER_PITCHING_STATS.WINS,
  [PITCHING_STATS.LOSSES]: PLAYER_PITCHING_STATS.LOSSES,
  [PITCHING_STATS.ERA]: PLAYER_PITCHING_STATS.ERA,
  [PITCHING_STATS.STRIKEOUTS]: PLAYER_PITCHING_STATS.STRIKEOUTS,
  [PITCHING_STATS.WALKS]: PLAYER_PITCHING_STATS.WALKS,
  [PITCHING_STATS.HITS_ALLOWED]: PLAYER_PITCHING_STATS.HITS_ALLOWED, // "hitsAllowed" -> "hits"
  [PITCHING_STATS.EARNED_RUNS]: PLAYER_PITCHING_STATS.EARNED_RUNS,
  [PITCHING_STATS.COMPLETE_GAMES]: PLAYER_PITCHING_STATS.COMPLETE_GAMES,
  [PITCHING_STATS.SHUTOUTS]: PLAYER_PITCHING_STATS.SHUTOUTS,
  [PITCHING_STATS.SAVES]: PLAYER_PITCHING_STATS.SAVES,
  [PITCHING_STATS.INNINGS_PITCHED]: PLAYER_PITCHING_STATS.INNINGS_PITCHED,
  [PITCHING_STATS.HOME_RUNS_ALLOWED]: PLAYER_PITCHING_STATS.HOME_RUNS_ALLOWED, // "homeRunsAllowed" -> "homeRuns"
};
